import * as THREE from "three";
import { LABEL_BG_COLOR, LABEL_TEXT_COLOR } from "./palette.js";

/**
 * Canvas-backed world-space text: draw once into a 2D canvas, upload as a
 * texture, put it on a `Sprite` (as the remote name tags do). Returns `null`
 * when there is no DOM — callers treat a missing label as normal.
 */

export interface Label {
  sprite: THREE.Sprite;
  dispose(): void;
}

export interface LabelOptions {
  /** CSS colour of the glyphs. */
  color?: string;
  /** World-space height of the sprite, metres. */
  heightM?: number;
  /** Font size in canvas pixels — only affects crispness, not world size. */
  fontPx?: number;
}

export function makeLabel(text: string, opts: LabelOptions = {}): Label | null {
  if (typeof document === "undefined") return null;

  const fontPx = opts.fontPx ?? 40;
  const heightM = opts.heightM ?? 0.5;
  const pad = Math.round(fontPx * 0.35);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (ctx === null) return null;

  const font = `600 ${fontPx}px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace`;
  ctx.font = font;
  const textW = Math.ceil(ctx.measureText(text).width);
  canvas.width = Math.min(1024, Math.max(32, textW + pad * 2));
  canvas.height = fontPx + pad * 2;

  // Resizing the canvas resets the 2D context, so the font has to be set again.
  ctx.font = font;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = LABEL_BG_COLOR;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = opts.color ?? LABEL_TEXT_COLOR;
  ctx.fillText(text, canvas.width * 0.5, canvas.height * 0.55);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;

  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthWrite: false,
    // Debug text must stay readable through the fog, or it is useless at the
    // exact distances you want to debug.
    fog: false,
  });

  const sprite = new THREE.Sprite(material);
  const aspect = canvas.width / canvas.height;
  sprite.scale.set(heightM * aspect, heightM, 1);
  sprite.renderOrder = 20;

  return {
    sprite,
    dispose(): void {
      sprite.removeFromParent();
      material.dispose();
      texture.dispose();
    },
  };
}
